import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { errorMessage, isApiError } from './api';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** Toast shown on success. */
      success?: string;
      /** Set to false when the caller renders the error itself (e.g. inline form errors). */
      errorToast?: boolean;
    };
  }
}

/** The app's client; tests build their own with `createQueryClient()` to get the same feedback. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // 4xx won't change on retry (and 404 means the endpoint isn't there yet); network/5xx might.
        retry: (count, error) => count < 2 && (!isApiError(error) || error.isTransient),
      },
    },
    // Default feedback for every mutation, so each form doesn't wire its own toasts.
    mutationCache: new MutationCache({
      onSuccess: (_data, _vars, _ctx, mutation) => {
        if (mutation.meta?.success) toast.success(mutation.meta.success);
      },
      onError: (error, _vars, _ctx, mutation) => {
        if (mutation.meta?.errorToast !== false) toast.error(errorMessage(error));
      },
    }),
  });
}

export const queryClient = createQueryClient();
